import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { mutateContentValue, setValue } from '@truqc/content-path';
import clone from 'lodash/clone';
import applyReducer, { reduceReducers } from './applyReducer';
import changeReducerFactory from './factories/changeReducerFactory';
import contentChangeReducerFactory from './factories/contentChangeReducerFactory';
import detailReducerFactory from './factories/detailReducerFactory';
import deferredConstants from '../constants/deferredConstants';
import entityReducerFactory from './factories/entityReducerFactory';
import uploadReducerFactory from './factories/uploadReducerFactory';
import formConstants from '../constants/formConstants';
import jobConstants from '../constants/jobConstants';
import annotationSelectionConstants from '../constants/annotationSelectionConstants';
const changeReducer = changeReducerFactory('form');
const contentChangeReducer = contentChangeReducerFactory('form');
const detailReducer = detailReducerFactory('form');
const entityReducer = entityReducerFactory('form');
const uploadReducer = uploadReducerFactory('form');
const initState = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, changeReducer.initState), contentChangeReducer.initState), detailReducer.initState), entityReducer.initState), uploadReducer.initState), {}, {
  permissions: {
    view: {
      include: ['job_edit']
    },
    edit: {
      include: ['job_edit']
    }
  },
  initialActive: {},
  downloadPDFPath: null
});
const clearEditorCache = state => _objectSpread(_objectSpread({}, state), {}, {
  editorCache: {}
});

// Starting a new form load should drop dirty state from whatever was active before
// (e.g. leaving a transient direct form for a cabinet form).
const clearPendingWhenLoadingNew = state => _objectSpread(_objectSpread({}, state), {}, {
  pendingChanges: false
});

// when a job fails on initial creation, we need to reset the related form
// so that it can be saved again.  This method does that.
const jobFail = (state, action) => {
  const _action$data$isNew = action.data.isNew,
    isNew = _action$data$isNew === void 0 ? false : _action$data$isNew;
  let result = state;
  if (isNew) {
    result = _objectSpread(_objectSpread({}, state), {}, {
      active: _objectSpread(_objectSpread({}, state.initialActive), {}, {
        // reset the active to the "initialActive" state, pre-save
        isNew: true
      }),
      pendingChanges: true,
      originalActive: {} // reset originalActive to generate the expected mergePatch on next save
    });
  }
  return result;
};
const setEditorCacheValue = (state, _ref) => {
  let name = _ref.name,
    value = _ref.value;
  // Create a deep copy of editorCache to avoid mutating nested objects
  // This ensures that rapid dispatches don't overwrite each other
  const editorCache = JSON.parse(JSON.stringify(state.editorCache || {}));
  mutateContentValue(name, editorCache, value);
  return _objectSpread(_objectSpread({}, state), {}, {
    editorCache
  });
};

/**
 * Batch update multiple cache values in a single dispatch.
 * This avoids multiple expensive JSON.parse(JSON.stringify()) operations
 * and prevents multiple re-renders from rapid dispatches.
 */
const setEditorCacheValues = (state, _ref2) => {
  let updates = _ref2.updates;
  if (!updates || updates.length === 0) {
    return state;
  }

  // Single deep copy for all updates
  const editorCache = JSON.parse(JSON.stringify(state.editorCache || {}));

  // Apply all updates to the same cache object
  updates.forEach(_ref3 => {
    let name = _ref3.name,
      value = _ref3.value;
    mutateContentValue(name, editorCache, value);
  });
  return _objectSpread(_objectSpread({}, state), {}, {
    editorCache
  });
};

/**
 *  'deferred' is just a special case of 'form' used by formSearchField. When a deferred form
 *  loads successfully, cache the record in the primary form.editorCache.
 *
 *  Note that the deferred form does not contain all the content data, just the id and title.
 *  We set overview: true so readReferencedContentBehavior can load full content from the server
 *  when the requested content path is missing.
 */
const setEditorCacheValueFromDeferred = (state, action) => {
  const id = action.id,
    _action$data = action.data,
    _action$data2 = _action$data === void 0 ? {} : _action$data,
    _action$data2$content = _action$data2.content,
    content = _action$data2$content === void 0 ? {} : _action$data2$content;
  const value = _objectSpread(_objectSpread({}, content), {}, {
    overview: true
  });
  return setEditorCacheValue(state, {
    name: "formCache/".concat(id),
    value
  });
};
const setInitialActive = (state, _ref4) => {
  let initialActive = _ref4.initialActive;
  return _objectSpread(_objectSpread({}, state), {}, {
    initialActive
  });
};
const restoreDirectFormSession = (state, _ref5) => {
  var _snapshot$pendingChan;
  let snapshot = _ref5.snapshot;
  const _ref6 = snapshot || {},
    active = _ref6.active;
  if (!(active !== null && active !== void 0 && active.id)) {
    return state;
  }
  return _objectSpread(_objectSpread({}, state), {}, {
    active,
    originalActive: clone(active),
    pendingChanges: (_snapshot$pendingChan = snapshot.pendingChanges) !== null && _snapshot$pendingChan !== void 0 ? _snapshot$pendingChan : true,
    loading: false,
    error: null
  });
};
const render = state => _objectSpread(_objectSpread({}, state), {}, {
  loading: true
});

// This way we can handle the error in the component for the active entity
const renderFail = state => _objectSpread(_objectSpread({}, state), {}, {
  loading: false
});

// replace the existing report in stores with the new report WITH signedURL
const renderSuccess = (state, action) => {
  var _action$data3;
  return _objectSpread(_objectSpread({}, state), {}, {
    active: _objectSpread(_objectSpread({}, state.active), {}, {
      signedURL: action === null || action === void 0 || (_action$data3 = action.data) === null || _action$data3 === void 0 ? void 0 : _action$data3.signedURL
    }),
    loading: false
  });
};
const handleBatchChange = (state, action) => {
  const changes = action.payload || [];
  if (!changes.length) return state;
  const active = state.active;
  if (!active || !active.content) {
    return state;
  }
  let newContent = _objectSpread({}, active.content);
  changes.forEach(_ref7 => {
    let path = _ref7.path,
      data = _ref7.data;
    // Remove /content prefix from path since changes are already in content format
    const contentPath = path.startsWith('/content/') ? path.replace('/content/', '/') : path;
    newContent = setValue(contentPath, newContent, data);
  });

  // Only update if content actually changed
  if (newContent === active.content) {
    return state;
  }
  return _objectSpread(_objectSpread({}, state), {}, {
    active: _objectSpread(_objectSpread({}, active), {}, {
      content: newContent
    }),
    pendingChanges: true
  });
};

/**
 * Clear the active form when it's deleted
 */
const handleFormDeleteSuccess = (state, action) => {
  const ids = action.ids;
  const deletedIds = Array.isArray(ids) ? ids : [ids];

  // If the currently active form is being deleted, clear it
  if (state.active && state.active.id && deletedIds.includes(state.active.id)) {
    return _objectSpread(_objectSpread({}, state), {}, {
      active: {},
      pendingChanges: false
    });
  }
  return state;
};
const clearSelection = (state, action) => {
  if (action.annotationContext === 'form') {
    return _objectSpread(_objectSpread({}, state), {}, {
      active: {}
    });
  }
  return state;
};
const renderAnnotatedSuccess = (state, _ref8) => {
  let downloadPDFPath = _ref8.data.downloadPDFPath;
  return _objectSpread(_objectSpread({}, state), {}, {
    downloadPDFPath
  });
};
const renderAnnotatedFailure = (state, action) => _objectSpread(_objectSpread({}, state), {}, {
  error: "Failed to render annotated form: ".concat(action.data.formID)
});
const clearDownloadPDFPath = state => _objectSpread(_objectSpread({}, state), {}, {
  downloadPDFPath: null
});
const reducers = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, changeReducer.reducers), detailReducer.reducers), entityReducer.reducers), contentChangeReducer.reducers), uploadReducer.reducers), {}, {
  [formConstants.CREATE]: reduceReducers(clearEditorCache, changeReducer.reducers[formConstants.CREATE], contentChangeReducer.reducers[formConstants.CREATE]),
  [formConstants.SET_INITIAL_ACTIVE]: setInitialActive,
  [formConstants.RESTORE_DIRECT_FORM_SESSION]: restoreDirectFormSession,
  [formConstants.SET_EDITOR_CACHE_VALUE]: setEditorCacheValue,
  [formConstants.SET_EDITOR_CACHE_VALUES]: setEditorCacheValues,
  [jobConstants.SAVE_FAIL]: jobFail,
  [formConstants.SHOW]: reduceReducers(clearEditorCache, clearPendingWhenLoadingNew, detailReducer.reducers[formConstants.SHOW]),
  [formConstants.RENDER]: render,
  [formConstants.RENDER_FAILURE]: renderFail,
  [formConstants.RENDER_SUCCESS]: renderSuccess,
  [deferredConstants.SHOW_SUCCESS]: setEditorCacheValueFromDeferred,
  [formConstants.BATCH_CHANGE]: handleBatchChange,
  [formConstants.DESTROY_SUCCESS]: handleFormDeleteSuccess,
  [formConstants.RENDER_ANNOTATED_SUCCESS]: renderAnnotatedSuccess,
  [formConstants.RENDER_ANNOTATED_FAILURE]: renderAnnotatedFailure,
  [formConstants.CLEAR_DOWNLOAD_PATH]: clearDownloadPDFPath,
  [annotationSelectionConstants.CLEAR_SELECTION]: clearSelection
});
export default applyReducer(reducers, initState);